import { GetStaticPaths, GetStaticPropsContext } from "next";
import { MenuType } from "@/types/baseTypes";
import { IconsType, LinkListOptionsType } from "@/types/ModuleOptionsType";
import {
  LabelsType,
  OptionsFooterType,
  OptionsSearchType,
  OptionsSocialMediaType,
} from "@/types/OptionType";
import {
  BilletoEventOverviewType,
  BillettoEventType,
  fetchAllBillettoEvents,
} from "@/getData/fetchBillettoData";
import { GlobalContextProvider } from "@aleksundshantu/aunds-utils-react/contexts";
import { Footer } from "@/components/Footer/Footer";
import { Navbar, NavbarOptionType } from "@/components/Navbar/Navbar";
import { Post } from "@/wp/types/client";
import { EventSingleViewHeader } from "@/components/Header/EventSingleViewHeader/EventSingleViewHeader";
import { ParentsType } from "@/wp/getParentPages";
import { EventSingleView } from "@/components/EventSingleView/EventSingleView";
import { getEventSingleViewProps } from "@/utils/eventsPage/getEventSingleViewProps.ts";

type PageProps = {
  pageData: Post;
  parents: ParentsType;
  legalMenu: MenuType;
  mainMenu: MenuType;
  searchOptions: OptionsSearchType;
  navbarOption: NavbarOptionType;
  footerMenu: MenuType;
  footerOptions: OptionsFooterType;
  socialMedia: OptionsSocialMediaType;
  labels: LabelsType;
  icons: IconsType;
  billettoEventData: BillettoEventType | null;
  billettoMoreEventsData: BilletoEventOverviewType[];
  linkListOptions: LinkListOptionsType | null;
};

export default function Page({
  pageData,
  parents,
  legalMenu,
  mainMenu,
  searchOptions,
  navbarOption,
  footerMenu,
  footerOptions,
  socialMedia,
  icons,
  labels,
  billettoEventData,
  billettoMoreEventsData,
  linkListOptions,
}: PageProps) {
  const mainstartLabel = labels?.find((label) => label.key === "skip_nav");
  const mainstartAriaLabel = labels?.find(
    (label) => label.key === "aria_label_mainstart",
  );

  if (billettoEventData == null) return <></>;

  return (
    <GlobalContextProvider iconsRaw={icons} labelsRaw={labels}>
      <div id="pagestart" tabIndex={-1} />

      {/* eslint-disable-next-line react/forbid-elements */}
      <a
        className="skipnav"
        href="#maincontent"
        {...(mainstartAriaLabel && {
          "aria-label": mainstartAriaLabel.value,
        })}
      >
        {mainstartLabel ? mainstartLabel.value : "Navigation überspringen"}
      </a>

      <Navbar
        searchPageSlug={searchOptions?.search_page_link || ""}
        menu={mainMenu}
        socialMedia={socialMedia.platforms}
        navbarOption={navbarOption}
        pageData={pageData}
        isHome={false}
        isPage={true}
        isTextHeader={false}
        isNobleElements={false}
      />

      <div id="maincontent" />
      <main id={`main-tag`}>
        <EventSingleViewHeader
          billettoData={billettoEventData}
          parents={parents}
        />

        <EventSingleView
          billettoEvent={billettoEventData}
          billettoMoreEvents={billettoMoreEventsData}
          linkListOptions={linkListOptions}
        />
      </main>

      <Footer
        autoShowConsentManagement={undefined}
        footerOptions={footerOptions}
        socialMedia={socialMedia}
        footerMenu={footerMenu}
        legalMenu={legalMenu}
      />
    </GlobalContextProvider>
  );
}

// export const getStaticPaths: GetStaticPaths = async () => {
//   return {
//     paths: [], // Keine Pfade beim Build generieren
//     fallback: "blocking", // Erst beim ersten Aufruf auf dem Server generieren
//   };
// };

// for referrence see recipe page at KleineTipTopf

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: { params: { id: string[] } }[] = [];
  const allEvents = await fetchAllBillettoEvents({});

  if (process.env.BUILD_ALL_POST && allEvents?.data) {
    allEvents.data.forEach((event) => {
      const eventId = event.id;
      paths.push({ params: { id: [eventId] } });
    });
  }

  return {
    paths,
    fallback: "blocking", // Or 'true' or 'false' depending on your strategy
  };
};

export async function getStaticProps(context: GetStaticPropsContext) {
  if (context.locale !== "de") {
    return {
      notFound: true,
      revalidate: parseInt(process.env.REVALIDATE_AFTER || "120"),
    };
  }

  return await getEventSingleViewProps(context);
}
